import { Injectable } from '@angular/core';
import { FormGroup, FormControl, FormArray } from '@angular/forms';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../../core/models/form-group-type';
import { isEqual, omit } from 'lodash-es';
import { ActionableGridEntity } from '../../../../../../core/models/grid/actionable-grid-entity';
import { DeletableGridEntity } from '../../../../../../core/models/grid/deletable-grid-entity';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { AssocCallSignBase } from '../../../schedule-h-additional-information/models/assoc-call-sign-base';
import { AssocControlPointBase } from '../../../schedule-h-additional-information/models/assoc-control-point-base';
import { FrequencyFormModel } from '../../../schedules-d-and-h/models/frequency-form-model';
import { ScheduleDFormModel } from '../../../schedules-d-and-h/models/schedule-d-form-model';
import { AntennaFormModel } from '../../../schedules-d-and-h/models/antenna-form-model';
import { EmissionDesignator } from '../../../schedules-d-and-h/models/emission-designator';
import { Frequency } from '../../../schedules-d-and-h/models/frequency';
import { ScheduleD } from '../../../schedules-d-and-h/models/schedule-d';
import { Antenna } from '../../../schedules-d-and-h/models/antenna';

@Injectable({
    providedIn: 'root',
})
export class SchedulesDAndHSharedFormService {
    readonly defaultOmitFields: (keyof (ActionableGridEntity &
        DeletableGridEntity))[] = ['actionCode', 'isDeleted'];

    // Retains the calculated action code while the form displays Delete.
    private readonly cachedActionCodeByForm = new WeakMap<
        FormGroup,
        ActionCode | null
    >();

    // Allows later edits to recalculate action codes after restore clears a persisted code.
    private readonly formsWithPersistedActionCleared = new WeakSet<FormGroup>();

    constructor() {}

    /**
     * Checks if the form value is equivalent to the response, ignoring specified fields.
     * @param form
     * @param response
     * @param omitFields a list of fields to omit when comparing the form and response values.
     * @returns true if the form is equivalent to the response, false otherwise.
     */
    isFormEquivalentToResponse(
        form:
            | FormGroup<FormGroupType<AssocControlPointBase>>
            | FormGroup<FormGroupType<AssocCallSignBase>>
            | FormGroup<FormGroupType<ScheduleDFormModel>>
            | FormGroup<FormGroupType<AntennaFormModel>>
            | FormGroup<NestedFormGroupType<FrequencyFormModel>>
            | FormGroup<FormGroupType<EmissionDesignator>>,
        response:
            | AssocControlPointBase
            | AssocCallSignBase
            | ScheduleDFormModel
            | AntennaFormModel
            | FrequencyFormModel
            | EmissionDesignator,
        omitFields: string[] = this.defaultOmitFields,
    ): boolean {
        return isEqual(
            omit(form.getRawValue(), omitFields),
            omit(response, omitFields),
        );
    }

    /**
     * Update the action code to the correct value based on the form value and the response from the API.
     * @param control
     * @param parentFormArray
     * @param response
     * @returns whether the action code was updated or not
     */
    markActionCodeOnChange(
        control: FormControl,
        parentFormArray:
            | FormArray<FormGroup<FormGroupType<AssocCallSignBase>>>
            | FormArray<FormGroup<FormGroupType<AssocControlPointBase>>>
            | FormArray<FormGroup<FormGroupType<ScheduleDFormModel>>>
            | FormArray<FormGroup<FormGroupType<AntennaFormModel>>>
            | FormArray<FormGroup<NestedFormGroupType<FrequencyFormModel>>>
            | FormArray<FormGroup<FormGroupType<EmissionDesignator>>>,
        response:
            | AssocCallSignBase[]
            | AssocControlPointBase[]
            | ScheduleDFormModel[]
            | AntennaFormModel[]
            | FrequencyFormModel[]
            | EmissionDesignator[],
        omitFields: string[] = this.defaultOmitFields,
    ): boolean {
        const matchingFormControl = parentFormArray.controls.find(
            (x) => x.value.id === control.parent?.value.id,
        );

        const matchingResponse = response.find(
            (x) => x.id === control.parent?.value.id,
        );

        // We don't want to change anything if the action code already has a value or when action code is Add
        if (
            !matchingResponse ||
            !matchingFormControl ||
            (matchingResponse.actionCode !== null &&
                matchingResponse.actionCode !== ActionCode.Delete &&
                !this.formsWithPersistedActionCleared.has(
                    matchingFormControl,
                )) ||
            matchingFormControl.controls.actionCode.value === ActionCode.Add
        ) {
            return false;
        }

        const actionCode = this.isFormEquivalentToResponse(
            matchingFormControl,
            matchingResponse,
            omitFields,
        )
            ? null
            : ActionCode.Modify;

        if (
            'isDeleted' in matchingFormControl.controls &&
            matchingFormControl.controls.isDeleted.value === true
        ) {
            this.cachedActionCodeByForm.set(matchingFormControl, actionCode);
            return false;
        }

        if (matchingFormControl.controls.actionCode.value === actionCode) {
            return false;
        }

        (matchingFormControl.controls.actionCode as FormControl).setValue(
            actionCode,
            { emitEvent: false },
        );

        return true;
    }

    /**
     * Updates the action code when a form is deleted or restored (undeleted).
     *
     * On delete, the current action code is cached and the form is marked Delete.
     * On restore, the cached value is used, except an action persisted by the API
     * becomes null because the deletion has been reversed.
     * @param form
     * @param response the API response rows for this form type
     * @returns whether the action code was updated or not
     */
    refreshFormActionCode(
        form:
            | FormGroup<FormGroupType<AssocCallSignBase>>
            | FormGroup<FormGroupType<AssocControlPointBase>>
            | FormGroup<FormGroupType<ScheduleDFormModel>>
            | FormGroup<FormGroupType<AntennaFormModel>>
            | FormGroup<NestedFormGroupType<FrequencyFormModel>>,
        response:
            | AssocCallSignBase[]
            | AssocControlPointBase[]
            | ScheduleD[]
            | Antenna[]
            | Frequency[]
            | EmissionDesignator[],
    ): boolean {
        // Don't need to toggle anything if the control point is new
        // also when the deleted control has not been touched by the user and the form is not deleted
        if (
            form.controls.actionCode.value === ActionCode.Add ||
            (form.controls.isDeleted.pristine &&
                form.controls.isDeleted.value !== true)
        ) {
            return false;
        }

        const currentActionCode = form.controls.actionCode.value;
        const hasCachedActionCode = this.cachedActionCodeByForm.has(form);
        const cachedActionCode = this.cachedActionCodeByForm.get(form);
        const isActionCodePersisted = this.isActionCodePersisted(
            form,
            response,
        );

        const actionCode = this.resolveActionCode(
            form.controls.isDeleted.value,
            currentActionCode,
            hasCachedActionCode,
            cachedActionCode,
            isActionCodePersisted,
        );

        if (form.controls.isDeleted.value) {
            // Keep the pre-delete action code available for the later restore transition.
            this.cachedActionCodeByForm.set(form, currentActionCode);
        } else {
            // The cached value applies to a single delete/restore cycle only.
            this.cachedActionCodeByForm.delete(form);
            if (isActionCodePersisted && actionCode === null) {
                this.formsWithPersistedActionCleared.add(form);
            }
        }

        (form.controls.actionCode as FormControl).setValue(actionCode, {
            emitEvent: false,
        });

        return currentActionCode !== form.controls.actionCode.value;
    }

    /**
     * Determines whether the form's action code is present in the last API response.
     * @param form the current form row
     * @param response the corresponding rows from the last API response
     * @returns true when the matching API row has an action code
     */
    private isActionCodePersisted(
        form:
            | FormGroup<FormGroupType<AssocCallSignBase>>
            | FormGroup<FormGroupType<AssocControlPointBase>>
            | FormGroup<FormGroupType<ScheduleDFormModel>>
            | FormGroup<FormGroupType<AntennaFormModel>>
            | FormGroup<NestedFormGroupType<FrequencyFormModel>>,
        response:
            | AssocCallSignBase[]
            | AssocControlPointBase[]
            | ScheduleD[]
            | Antenna[]
            | Frequency[]
            | EmissionDesignator[],
    ): boolean {
        const id = form.controls.id.value;
        if (id === null) {
            return false;
        }

        return response.some(
            (item) => item.id === id && item.actionCode !== null,
        );
    }

    /**
     * Determines the action code for a form during a delete/restore transition.
     * @param isDeleted whether the form is currently marked as deleted
     * @param currentActionCode the form's action code before this transition
     * @param hasCachedActionCode whether an action code was cached before deletion
     * @param cachedActionCode the cached action code, if any
     * @param isActionCodePersisted whether the API response has an action code for this form
     */
    private resolveActionCode(
        isDeleted: boolean,
        currentActionCode: ActionCode | null,
        hasCachedActionCode: boolean,
        cachedActionCode: ActionCode | null | undefined,
        isActionCodePersisted: boolean,
    ): ActionCode | null {
        if (isDeleted) {
            return ActionCode.Delete;
        }

        if (hasCachedActionCode) {
            // A cached Delete is always cleared on restore.
            if (cachedActionCode === ActionCode.Delete) {
                return null;
            }

            return isActionCodePersisted ? null : cachedActionCode!;
        }

        // API-loaded Delete rows have no cached action code before their first restore.
        if (currentActionCode === ActionCode.Delete && isActionCodePersisted) {
            return null;
        }

        return currentActionCode;
    }
}
